import { getWeldStatusCalculationMethod } from '../../../utils/statusUtils';
import type { AnomalyDetail, WeldAnalyticsBundle, WeldSummary } from '../types/weld';
import type { OverviewWeldRow } from '../components/WeldOverviewTable';
import { avgDurationSec, deltaPct, getMonthBoundaries, passFailCounts, weldsInRange } from './monthBuckets';
import { buildDynamicDurationBins, buildDurationScatter } from './chartHelpers';
import type { DurationScatterPoint } from './chartHelpers';
import { filterWeldsByJobNumbers, filterWeldsByJobRefs } from './jobFilter';
import { formatAnomalyDisplayTitle } from './formatAnomalyDisplay';

export type WeldOutcome = 'pass' | 'fail' | 'other';

export interface NormalizedWeld {
  weldNumber: string;
  sequence: number;
  outcome: WeldOutcome;
  durationSec: number | null;
  qualityScore: number | null;
  startTimeMs: number | null;
  endTimeMs: number | null;
  jobNumber: string;
  pipeId?: string | null;
  wpsNumber: string | null;
  weldingSystem: string | null;
  shift: string | null;
}

export interface DashboardKpis {
  totalWelds: number;
  passedWelds: number;
  failedWelds: number;
  passRatePct: number | null;
  avgDurationSec: number | null;
  fastestDurationSec: number | null;
  slowestDurationSec: number | null;
  avgQualityScore: number | null;
  passedDelta: ReturnType<typeof deltaPct>;
  failedDelta: ReturnType<typeof deltaPct>;
  durationDelta: ReturnType<typeof deltaPct>;
}

export interface DurationBarPoint {
  weldNumber: string;
  durationSec: number;
  isOutlier: boolean;
}

export interface QualityTrendPoint {
  sequence: number;
  weldNumber: string;
  jobNumber: string;
  qualityScore: number;
  timestampMs: number | null;
}

export interface TimelinePoint {
  weldNumber: string;
  jobNumber: string;
  outcome: WeldOutcome;
  durationSec: number | null;
  qualityScore: number | null;
  timestampMs: number | null;
  pipeId: string | null;
}

export interface FailureReasonCount {
  reason: string;
  count: number;
}

export interface FailureAnalysis {
  totalFailed: number;
  failurePct: number | null;
  mostCommonReason: string | null;
  avgFailureDurationSec: number | null;
  reasons: FailureReasonCount[];
}

export interface DurationHistogramBin {
  label: string;
  count: number;
  /** Inclusive lower bound in seconds. */
  loSec: number;
  /** Exclusive upper bound in seconds; null means open-ended through max. */
  hiSec: number | null;
}

export interface ProductionSummary {
  totalProductionTimeSec: number | null;
  avgTimeBetweenWeldsSec: number | null;
  productivityWeldsPerHour: number | null;
  longestIdleTimeSec: number | null;
  totalActiveWeldingTimeSec: number | null;
}

export interface DashboardMetrics {
  welds: NormalizedWeld[];
  kpis: DashboardKpis;
  passFailDonut: { name: string; value: number; color: string }[];
  durationBars: DurationBarPoint[];
  durationScatter: DurationScatterPoint[];
  qualityTrend: QualityTrendPoint[];
  timeline: TimelinePoint[];
  failureAnalysis: FailureAnalysis;
  durationHistogram: DurationHistogramBin[];
  productionSummary: ProductionSummary;
  headerDateLabel: string;
  lastUpdatedMs: number | null;
  hasQualityData: boolean;
}

function parseDurationSec(row: OverviewWeldRow): number | null {
  const raw = (row as { weld_time?: number | null }).weld_time ?? row.weldTime;
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw * 60;
  const match = String(raw).match(/(-?\d+\.?\d*)/);
  if (!match) return null;
  const minutes = parseFloat(match[1]);
  return Number.isFinite(minutes) ? minutes * 60 : null;
}

/**
 * Prefer nullish coalescing (not ||) so boolean `false` (out of limit) is kept.
 * Shared getWeldStatusCalculationMethod uses || and can drop falsey statuses.
 */
function rawStatusForOutcome(row: OverviewWeldRow, statusMethod: string): unknown {
  switch (statusMethod) {
    case 'AvgByPass':
      return row.pass_avg_status ?? row.statusByAvgPass;
    case 'AvgByZone':
      return row.pass_staus_by_zone ?? row.statusByAvgZone;
    case 'AvgByTilt':
      return row.statusByAvgTilt;
    case 'Instantaneous':
    default:
      return row.pass_status_by_instantaneous ?? row.statusByInstantaneous;
  }
}

function resolveOutcome(row: OverviewWeldRow, statusMethod: string): WeldOutcome {
  const raw = rawStatusForOutcome(row, statusMethod);
  // Boolean from weld-status API.
  if (raw === true) return 'pass';
  if (raw === false) return 'fail';
  const s = raw == null ? '' : String(raw).toUpperCase();
  if (s === 'TRUE' || s === 'COMPLETED' || s === 'PASS' || s === 'PASSED' || s === '1') return 'pass';
  if (s === 'FALSE' || s === 'REJECTED' || s === 'FAIL' || s === 'FAILED' || s === '0' || s === 'CROSS') {
    return 'fail';
  }
  // NULL / empty / unknown → not counted as within or out of limit.
  return 'other';
}

function formatJobNumber(jn: string[] | string | undefined): string {
  if (!jn) return '';
  if (Array.isArray(jn)) return jn[0] ?? '';
  return jn;
}

function qualityFromBundleSummary(summary: WeldSummary | undefined): number | null {
  if (!summary) return null;
  if (summary.anomaly_pct != null && Number.isFinite(summary.anomaly_pct)) {
    return Math.max(0, Math.min(100, 100 - summary.anomaly_pct));
  }
  if (summary.composite_score != null && Number.isFinite(summary.composite_score)) {
    const normalized = summary.composite_score <= 1 ? summary.composite_score : summary.composite_score / 100;
    return Math.max(0, Math.min(100, (1 - normalized) * 100));
  }
  return null;
}

function weldNumberSortKey(n: string): number {
  const parsed = parseFloat(n);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function shiftFromTimestamp(ms: number | null): string | null {
  if (ms == null) return null;
  const hour = new Date(ms).getHours();
  if (hour >= 6 && hour < 14) return 'Day';
  if (hour >= 14 && hour < 22) return 'Evening';
  return 'Night';
}

function buildBundleIndex(bundle: WeldAnalyticsBundle | null): Map<string, WeldSummary> {
  const map = new Map<string, WeldSummary>();
  bundle?.welds?.forEach((w) => {
    const key = `${w.job_number}::${w.weld_number}`;
    map.set(key, w);
    map.set(w.weld_number, w);
  });
  return map;
}

export function normalizeWelds(
  productionRows: OverviewWeldRow[],
  bundle: WeldAnalyticsBundle | null,
  statusMethod: string,
): NormalizedWeld[] {
  const bundleIndex = buildBundleIndex(bundle);

  const sorted = [...productionRows].sort((a, b) => {
    const ta = a.startTime != null ? Number(a.startTime) * 1000 : 0;
    const tb = b.startTime != null ? Number(b.startTime) * 1000 : 0;
    if (ta !== tb) return ta - tb;
    return weldNumberSortKey(String(a.weldNumber ?? '')) - weldNumberSortKey(String(b.weldNumber ?? ''));
  });

  return sorted.map((row, idx) => {
    const weldNumber = String(row.weldNumber ?? `W${idx + 1}`);
    const jobNumber = formatJobNumber(row.job_number as string[] | string | undefined);
    const bundleWeld =
      bundleIndex.get(`${jobNumber}::${weldNumber}`) ?? bundleIndex.get(weldNumber);
    const startRaw = row.startTime != null ? Number(row.startTime) : null;
    const endRaw = row.endTime != null ? Number(row.endTime) : null;

    const wpsRaw = (row as { wpsNumber?: string }).wpsNumber;
    const rowPipe = (row as { pipeId?: string | number | null; pipe_details_id?: string | number | null });
    const pipeRaw = rowPipe.pipeId ?? rowPipe.pipe_details_id;

    return {
      weldNumber,
      sequence: idx + 1,
      outcome: resolveOutcome(row, statusMethod),
      durationSec: parseDurationSec(row),
      qualityScore: qualityFromBundleSummary(bundleWeld),
      startTimeMs: startRaw != null && Number.isFinite(startRaw) ? startRaw * 1000 : null,
      endTimeMs: endRaw != null && Number.isFinite(endRaw) ? endRaw * 1000 : null,
      jobNumber,
      pipeId: pipeRaw != null && String(pipeRaw).trim() !== '' ? String(pipeRaw) : null,
      wpsNumber: wpsRaw != null && String(wpsRaw).trim() !== '' ? String(wpsRaw) : null,
      weldingSystem: bundleWeld?.welding_system ?? null,
      shift: shiftFromTimestamp(
        startRaw != null && Number.isFinite(startRaw) ? startRaw * 1000 : null,
      ),
    };
  });
}

function meanAndStd(values: number[]): { mean: number; std: number } {
  if (values.length === 0) return { mean: 0, std: 0 };
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return { mean, std: Math.sqrt(variance) };
}

function formatDateRangeLabel(welds: NormalizedWeld[]): string {
  const times = welds.map((w) => w.startTimeMs).filter((t): t is number => t != null);
  if (times.length === 0) return '—';
  const min = Math.min(...times);
  const max = Math.max(...times);
  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  if (fmt(min) === fmt(max)) return fmt(min);
  return `${fmt(min)} – ${fmt(max)}`;
}

function failureReasonFromAnomaly(a: AnomalyDetail): string {
  return formatAnomalyDisplayTitle(a);
}

function computeFailureAnalysis(welds: NormalizedWeld[], anomalies: AnomalyDetail[]): FailureAnalysis {
  const failedWelds = welds.filter((w) => w.outcome === 'fail');
  const totalFailed = failedWelds.length;
  const failurePct = welds.length > 0 ? (totalFailed / welds.length) * 100 : null;

  const failedDurations = failedWelds
    .map((w) => w.durationSec)
    .filter((d): d is number => d != null);
  const avgFailureDurationSec =
    failedDurations.length > 0
      ? failedDurations.reduce((s, v) => s + v, 0) / failedDurations.length
      : null;

  const reasonCounts = new Map<string, number>();
  anomalies.forEach((a) => {
    const reason = failureReasonFromAnomaly(a);
    reasonCounts.set(reason, (reasonCounts.get(reason) ?? 0) + 1);
  });
  if (reasonCounts.size === 0 && failedWelds.length > 0) {
    reasonCounts.set('Weld rejected', failedWelds.length);
  }

  const reasons = Array.from(reasonCounts.entries())
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return {
    totalFailed,
    failurePct,
    mostCommonReason: reasons[0]?.reason ?? null,
    avgFailureDurationSec,
    reasons,
  };
}

function computeProductionSummary(welds: NormalizedWeld[]): ProductionSummary {
  const withTimes = welds.filter((w) => w.startTimeMs != null).sort((a, b) => (a.startTimeMs ?? 0) - (b.startTimeMs ?? 0));
  const durations = welds.map((w) => w.durationSec).filter((d): d is number => d != null);

  const totalActiveWeldingTimeSec =
    durations.length > 0 ? durations.reduce((s, v) => s + v, 0) : null;

  let totalProductionTimeSec: number | null = null;
  let avgTimeBetweenWeldsSec: number | null = null;
  let longestIdleTimeSec: number | null = null;

  if (withTimes.length >= 2) {
    const first = withTimes[0].startTimeMs!;
    const last = withTimes[withTimes.length - 1].endTimeMs ?? withTimes[withTimes.length - 1].startTimeMs!;
    totalProductionTimeSec = (last - first) / 1000;

    const gaps: number[] = [];
    for (let i = 1; i < withTimes.length; i += 1) {
      const prevEnd = withTimes[i - 1].endTimeMs ?? withTimes[i - 1].startTimeMs!;
      const currStart = withTimes[i].startTimeMs!;
      if (prevEnd != null && currStart != null) {
        gaps.push(Math.max(0, (currStart - prevEnd) / 1000));
      }
    }
    if (gaps.length > 0) {
      avgTimeBetweenWeldsSec = gaps.reduce((s, v) => s + v, 0) / gaps.length;
      longestIdleTimeSec = Math.max(...gaps);
    }
  }

  const productivityWeldsPerHour =
    totalProductionTimeSec != null && totalProductionTimeSec > 0
      ? (welds.length / totalProductionTimeSec) * 3600
      : null;

  return {
    totalProductionTimeSec,
    avgTimeBetweenWeldsSec,
    productivityWeldsPerHour,
    longestIdleTimeSec,
    totalActiveWeldingTimeSec,
  };
}

function computeKpiDeltas(
  productionRows: OverviewWeldRow[],
  statusMethod: string,
  now: Date,
): Pick<DashboardKpis, 'passedDelta' | 'failedDelta' | 'durationDelta'> {
  const { currentStart, currentEnd, prevStart, prevEnd } = getMonthBoundaries(now);
  const currentRows = weldsInRange(productionRows, currentStart, currentEnd, 'seconds');
  const previousRows = weldsInRange(productionRows, prevStart, prevEnd, 'seconds');
  const getStatus = (w: OverviewWeldRow) => getWeldStatusCalculationMethod(statusMethod, w);
  const currentCounts = passFailCounts(currentRows, getStatus);
  const previousCounts = passFailCounts(previousRows, getStatus);
  const currentAvg = avgDurationSec(currentRows);
  const previousAvg = avgDurationSec(previousRows);

  return {
    passedDelta: deltaPct(currentCounts.passed, previousCounts.passed),
    failedDelta: deltaPct(currentCounts.failed, previousCounts.failed),
    durationDelta: deltaPct(currentAvg, previousAvg),
  };
}

export function computeDashboardMetrics(
  productionRows: OverviewWeldRow[],
  bundle: WeldAnalyticsBundle | null,
  statusMethod: string,
  selectedJobNumbers: string[] = [],
  selectedJobRefs: import('./jobNumbersFromSetup').PipeJobRef[] = [],
): DashboardMetrics {
  const scopedRows =
    selectedJobRefs.length > 0
      ? filterWeldsByJobRefs(productionRows as import('./jobFilter').WeldRowWithPipe[], selectedJobRefs)
      : selectedJobNumbers.length > 0
        ? filterWeldsByJobNumbers(productionRows, selectedJobNumbers)
        : productionRows;

  const welds = normalizeWelds(scopedRows, bundle, statusMethod);
  const durations = welds.map((w) => w.durationSec).filter((d): d is number => d != null);
  const qualityScores = welds.map((w) => w.qualityScore).filter((q): q is number => q != null);

  const passedWelds = welds.filter((w) => w.outcome === 'pass').length;
  const failedWelds = welds.filter((w) => w.outcome === 'fail').length;
  const otherWelds = welds.filter((w) => w.outcome === 'other').length;
  const counted = passedWelds + failedWelds;
  const passRatePct = counted > 0 ? (passedWelds / counted) * 100 : null;

  const avgDuration =
    durations.length > 0 ? durations.reduce((s, v) => s + v, 0) / durations.length : null;
  const fastestDurationSec = durations.length > 0 ? Math.min(...durations) : null;
  const slowestDurationSec = durations.length > 0 ? Math.max(...durations) : null;
  const avgQualityScore =
    qualityScores.length > 0 ? qualityScores.reduce((s, v) => s + v, 0) / qualityScores.length : null;

  const { mean, std } = meanAndStd(durations);
  const outlierThreshold = mean + Math.max(std, mean * 0.5);

  const deltas = computeKpiDeltas(scopedRows, statusMethod, new Date());

  const lastUpdatedMs = welds.reduce<number | null>((max, w) => {
    const t = w.endTimeMs ?? w.startTimeMs;
    if (t == null) return max;
    return max == null ? t : Math.max(max, t);
  }, null);

  return {
    welds,
    kpis: {
      totalWelds: welds.length,
      passedWelds,
      failedWelds,
      passRatePct,
      avgDurationSec: avgDuration,
      fastestDurationSec,
      slowestDurationSec,
      avgQualityScore,
      ...deltas,
    },
    // Include unevaluated ("other") so the donut is not blank when welds exist
    // but WPS status fields are still NULL / missing.
    passFailDonut: [
      { name: 'Within limit', value: passedWelds, color: '#1F9A3A' },
      { name: 'Out of limit', value: failedWelds, color: '#EB1C37' },
      { name: 'Other', value: otherWelds, color: '#94A3B8' },
    ].filter((d) => d.value > 0),
    durationBars: welds
      .filter((w) => w.durationSec != null)
      .map((w) => ({
        weldNumber: w.weldNumber,
        durationSec: w.durationSec!,
        isOutlier: w.durationSec! > outlierThreshold,
      })),
    durationScatter: buildDurationScatter(welds),
    qualityTrend: welds
      .filter((w) => w.qualityScore != null)
      .map((w) => ({
        sequence: w.sequence,
        weldNumber: w.weldNumber,
        jobNumber: w.jobNumber,
        qualityScore: w.qualityScore!,
        timestampMs: w.startTimeMs,
      })),
    timeline: welds.map((w) => ({
      weldNumber: w.weldNumber,
      jobNumber: w.jobNumber,
      outcome: w.outcome,
      durationSec: w.durationSec,
      qualityScore: w.qualityScore,
      timestampMs: w.startTimeMs,
      pipeId: w.pipeId ?? null,
    })),
    failureAnalysis: computeFailureAnalysis(welds, bundle?.anomalies ?? []),
    durationHistogram: buildDynamicDurationBins(durations),
    productionSummary: computeProductionSummary(welds),
    headerDateLabel: formatDateRangeLabel(welds),
    lastUpdatedMs,
    hasQualityData: qualityScores.length > 0,
  };
}
